import { autoinject, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { AchToken } from '../../../models/ach-token';
import { BankAccount } from '../../../models/bank-account';
import { CardToken } from '../../../models/card-token';
import { CreditCard } from '../../../models/credit-card';
import { PaymentMethodTypeOptions } from '../../../models/payment-method-type-options';
import { State } from '../../../services/global-state';
import { PayrazrService } from '../../../services/payrazr';
import { ValidationHelpers } from '../../../services/validation-helpers';

@autoinject
export class SetupPrimaryPaymentMethod {
	creditCard: any;
	bankAccount: any;
	walletItems: any;
	selectedPaymentMethodType: any;
	selectedPaymentMethod: any;
	validationErrors: any;
	setPrimaryPaymentMethodExceptionMessage: any;
	redirectTo: any;

	constructor(private payrazr: PayrazrService,
		private globalState: State,
		private router: Router,
		private i18n: I18N) {
		this.creditCard = new CreditCard();
		this.bankAccount = new BankAccount();
		this.walletItems = [];
		this.selectedPaymentMethodType = -1;
		this.selectedPaymentMethod = -1;
		this.validationErrors = [];

		this.setPrimaryPaymentMethodExceptionMessage = 'Error encountered while setting primary payment method. Please try again later. \n\nYou can set up your primary payment method by acccessing the \'My Profile\' menu.';
	}

	activate() {
		this.getCustomerPaymentMethods(this.globalState.customer.id);
		this.globalState.associatedCustomers.forEach(customer => {
			this.getCustomerPaymentMethods(customer.id);
		});
	}

	getCustomerPaymentMethods(customerId: string) {
		this.payrazr.getCustomerPaymentMethodsByCustomerId(customerId).then(response => {
			if (response.isSuccessful) {
				let bankAccounts = [];
				let creditCards = [];

				bankAccounts = response.entity.achPaymentMethods;
				creditCards = response.entity.creditCardPaymentMethods;

				let savedPaymentMethods = [...bankAccounts, ...creditCards];

				this.walletItems = this.walletItems.concat(savedPaymentMethods);
			} else {
				console.log(response.message);
			}
		}).catch(err => {
			console.log('Failed to retrieve customer payment methods');
		});
	}

	@computedFrom('walletItems')
	get paymentMethodTypeOptions() {
		let options = new PaymentMethodTypeOptions();

		if (this.walletItems.length < 1) {
			options.showWallet = false;
		}

		options.walletLabel = this.i18n.i18next.t('selectFromWallet', 'Select From Wallet');
		options.cardLabel = this.i18n.i18next.t('addNewCreditCard', 'Add New Credit Card');
		options.achLabel = this.i18n.i18next.t('addNewECheck', 'Add New E-Check');

		return options;
	}

	cancel() {
		let path = 'notificationPreferences';
		if (!this.redirectTo || this.redirectTo === 'dashboard') {
			path = 'secure';
		}
		this.router.navigateToRoute(path);
	}

	save() {
		this.validationErrors = [];

		switch (this.selectedPaymentMethodType.name) {
			case 'wallet':
				this.setPrimaryPaymentMethod();
				break;
			case 'card':
				this.saveCard();
				break;
			case 'ach':
				this.saveCheck();
				break;
			default:
				this.validationErrors.push(this.i18n.i18next.t('mustAddOrSelectPrimaryPaymentMethod', 'You must add or select a primary payment method.'));
				return;
		}
	}

	setPrimaryPaymentMethod() {
		if (this.selectedPaymentMethod === '-1') {
			this.validationErrors.push(this.i18n.i18next.t('mustAddOrSelectPrimaryPaymentMethod', 'You must add or select a primary payment method.'));
			return;
		}

		let paymentInfoKey = -1;
		let paymentType = '';

		if ('cardInfoKey' in this.selectedPaymentMethod && !!this.selectedPaymentMethod.cardInfoKey) {
			paymentInfoKey = this.selectedPaymentMethod.cardInfoKey;
			paymentType = 'credit';
		}
		else {
			paymentInfoKey = this.selectedPaymentMethod.checkInfoKey;
			paymentType = 'check';
		}

		let _this = this;

		let customerId = this.getCustomerId();

		this.payrazr.setCustomerPrimaryPaymentMethod(paymentInfoKey, paymentType, customerId).then(response => {
			if (response.isSuccessful) {
				this.globalState.customer.hasPrimaryPaymentMethod = true;
				// TODO: When feature toggle PayableSchedule get's removed just implement ELSE statement
				if (!this.globalState.features.payableSchedule) {
					this.sendCustomerInvoice(_this);
				}
				else {
					this.redirectToDashboard();
				}
			}
			else {
				console.log(response.message);
				alert(this.setPrimaryPaymentMethodExceptionMessage);
				this.redirectToDashboard();
			}
		})
			.catch(err => {
				console.log('Error occurred while setting primary payment method.');
				alert(this.setPrimaryPaymentMethodExceptionMessage);
				this.redirectToDashboard();
			});
	}

	saveCheck() {
		if (!this.isValidCheckForm()) {
			return;
		}

		let achToken = new AchToken();
		achToken.accountName = this.bankAccount.billingName;
		achToken.aba = this.bankAccount.bankRoutingNumber;
		achToken.accountNumber = this.bankAccount.bankAccountNumber;
		achToken.accountType = this.bankAccount.bankAccountType;
		achToken.zipCode = this.bankAccount.billingZipcode;
		let _this = this;

		let customerId = this.getCustomerId();

		this.payrazr.addAchPaymentMethod(achToken, true, customerId).then(response => {
			if (response.isSuccessful) {
				// TODO: When feature toggle PayableSchedule get's removed just implement ELSE statement
				if (!this.globalState.features.payableSchedule) {
					this.sendCustomerInvoice(_this);
				}
				else {
					this.redirectToDashboard();
				}
			} else {
				alert(response.message);
				this.redirectToDashboard();
			}
		}).catch(error => {
			console.log('error encountered while saving payment method.');
			alert(this.setPrimaryPaymentMethodExceptionMessage);
			this.redirectToDashboard();
		});
	}

	saveCard() {
		if (!this.isValidCreditCardForm()) {
			return;
		}

		let cardToken = new CardToken();
		cardToken.cardholderName = this.creditCard.billingName;
		cardToken.cardNumber = this.creditCard.creditCardNumber;
		cardToken.expirationDate = this.creditCard.expirationDate;
		cardToken.zipCode = this.creditCard.billingZipcode;
		let _this = this;

		let customerId = this.getCustomerId();

		this.payrazr.addCardPaymentMethod(cardToken, true, customerId).then(response => {
			if (response.isSuccessful) {
				// TODO: When feature toggle PayableSchedule get's removed just implement ELSE statement
				if (!this.globalState.features.payableSchedule) {
					this.sendCustomerInvoice(_this);
				}
				else {
					this.redirectToDashboard();
				}
			} else {
				alert(response.message);
				this.redirectToDashboard();
			}
		}).catch(error => {
			console.log('error encountered while saving payment method.');
			alert(this.setPrimaryPaymentMethodExceptionMessage);
			this.redirectToDashboard();
		});
	}

	isValidCreditCardForm() {
		if (!ValidationHelpers.isValidCreditCardNumber(this.creditCard.creditCardNumber)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidCreditCardNumber','Invalid credit card number.'));
		}

		if (!ValidationHelpers.isValidExpirationDate(this.creditCard.expirationDate)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidExpirationDate','Invalid expiration date.'));
		}

		if (!ValidationHelpers.isValidBillingName(this.creditCard.billingName)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidCardholderName','Invalid cardholder name.'));
		}

		if (!ValidationHelpers.isZipCode(this.creditCard.billingZipcode)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidZipCode','Invalid zipcode.'));
		}

		return this.validationErrors.length < 1;
	}

	isValidCheckForm() {
		if (!ValidationHelpers.isTransitNumber(this.bankAccount.bankRoutingNumber)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidBankRoutingNumber','Invalid bank routing number.'));
		}

		if (!this.bankAccount.bankAccountNumber || this.bankAccount.bankAccountNumber.length < 0) {
			this.validationErrors.push(this.i18n.i18next.t('invalidBankAccountNumber','Invalid bank account number.'));
		}

		if (!ValidationHelpers.isValidBillingName(this.bankAccount.billingName)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidAccountHolderName','Invalid account holder name.'));
		}

		if (!ValidationHelpers.isZipCode(this.bankAccount.billingZipcode)) {
			this.validationErrors.push(this.i18n.i18next.t('invalidZipCode','Invalid zipcode.'));
		}

		return this.validationErrors.length < 1;
	}

	sendCustomerInvoice(context) {
		let customerId = this.getCustomerId();

		this.payrazr.sendCustomerInvoice(customerId).then(response => {
			if (response.isSuccessful) {
				alert(this.i18n.i18next.t('nowEnrolledToMakePaymentsViaText', 'You are now enrolled to make payments via text'));
			}
			else {
				alert(response.message);
			}
		}).catch(error => {
			console.log('error encountered while sending customer invoice.');
		}).then(() => {
			context.redirectToDashboard();
		});
	}

	redirectToDashboard() {
		this.router.navigateToRoute('secure');
	}

	getCustomerId(): string {
		return this.globalState.associatedCustomers.length ?
			this.globalState.associatedCustomers[this.globalState.associatedCustomers.length - 1].id :
			this.globalState.customer.id;
	}
}
